import { computed, readonly, ref } from "vue";
import { cuandoSeAcabeElCredito } from "@/api/client";

/**
 * «Llegó al límite de uso», para toda la app.
 *
 * Es hermano de `useSesion` en la forma y contrario en el fondo: aquel avisa de
 * algo que solo una persona puede componer; éste avisa de algo que **nadie**
 * puede componer y que se compone solo. Por eso no trae botón de arreglar — trae
 * la hora a la que vuelve, que es lo único accionable: saber si te esperas o te
 * vas a hacer otra cosa.
 *
 * Se prende desde la capa de HTTP (cualquier petición que reciba `LIMITE_DE_USO`)
 * y se apaga solo al llegar la hora. Si el proveedor no dijo hora, se apaga a los
 * quince minutos: quedarse encendido para siempre por un aviso viejo es peor que
 * apagarse temprano, porque la siguiente corrida que truene lo vuelve a prender.
 */
const ESPERA_A_CIEGAS_MS = 15 * 60_000;

const mensaje = ref("");
const reponeEn = ref<Date | null>(null);
const hasta = ref<number>(0);
/** Se mueve con un reloj para que la cuenta regresiva se repinte sola. */
const ahora = ref(Date.now());

let reloj: ReturnType<typeof setInterval> | null = null;
let enganchado = false;

function prender(detalle: { mensaje: string; reponeEn: string | null }): void {
  mensaje.value = detalle.mensaje;
  const fecha = detalle.reponeEn ? new Date(detalle.reponeEn) : null;
  const valida = fecha && !Number.isNaN(fecha.getTime()) ? fecha : null;

  reponeEn.value = valida;
  hasta.value = valida ? valida.getTime() : Date.now() + ESPERA_A_CIEGAS_MS;
  ahora.value = Date.now();

  reloj ??= setInterval(() => {
    ahora.value = Date.now();
    if (ahora.value >= hasta.value) apagar();
  }, 1000);
}

function apagar(): void {
  mensaje.value = "";
  reponeEn.value = null;
  hasta.value = 0;
  if (reloj) {
    clearInterval(reloj);
    reloj = null;
  }
}

export function useLimite() {
  // Se engancha una sola vez, aunque el composable se use en varias pantallas.
  if (!enganchado) {
    enganchado = true;
    cuandoSeAcabeElCredito(prender);
  }

  const activo = computed(() => hasta.value > 0 && ahora.value < hasta.value);

  /** «12 minutos», «1 h 05 min» — lo que falta, ya redactado. */
  const faltan = computed(() => {
    if (!activo.value) return "";
    const segundos = Math.max(0, Math.round((hasta.value - ahora.value) / 1000));
    if (segundos < 60) return `${String(segundos)} s`;
    const minutos = Math.floor(segundos / 60);
    if (minutos < 60) return `${String(minutos)} min`;
    const horas = Math.floor(minutos / 60);
    return `${String(horas)} h ${String(minutos % 60).padStart(2, "0")} min`;
  });

  /** La hora del reloj, cuando el proveedor la dijo. */
  const horaDeVuelta = computed(() =>
    reponeEn.value
      ? reponeEn.value.toLocaleTimeString("es-MX", { hour: "numeric", minute: "2-digit" })
      : "",
  );

  return {
    activo,
    mensaje: readonly(mensaje),
    faltan,
    horaDeVuelta,
    /** Para el día que alguien quiera quitarlo a mano. */
    apagar,
  };
}
